/**
 * XPEN-3563: Build a line's `amounts_summary` block exactly as the server does.
 *
 * Lines on expense-report invoices carry this pre-calculated money block. The
 * expense-report sync and the approval screen read their figures from it, so a
 * line whose amounts change (such as one part of a split line) needs a new one.
 *
 * Mirrors xpens-api `_compute_amounts_summary` (`src/utils/expense_invoice_processor.py`).
 * Not mirrored: the simpler `_build_item_amounts_summary` (`src/utils/rules_processor.py`),
 * which never computes the non-recoverable tax and rounds the total with tax.
 *
 * Like the server, the total with tax and the subtotal are not rounded again after
 * the addition or subtraction.
 *
 * A tax deduction of 100% or more throws a RangeError. At exactly 100% the server
 * divides by zero and raises, so it builds no block either. Above 100% (outside the
 * rule editor's -100 to 100 input range) the server turns a positive tax into a
 * negative deducted amount, which a positive `TaxDeducted` never matches, so the
 * deduction would be dropped. Refusing both means the caller never saves a block
 * that silently loses it.
 *
 * @param {object} item - The invoice line (`Total`, `SubTotal`, `Tax1`, `Tax2`, `Tax`,
 *   `TipAllocated`, `TaxDeducted`).
 * @param {number} [taxDeduction] - The invoice's `TaxDeduction`.
 * @returns {object} The eight `amounts_summary` keys, as numbers, never `NaN`.
 * @throws {RangeError} When `taxDeduction` is 1 (100%) or more.
 */
export function buildAmountsSummary(item, taxDeduction) {
  const line = item || {};
  const total = round2(readNumber(line.Total, 0));
  const tax1 = round2(readNumber(line.Tax1, 0));
  const tax2 = round2(readNumber(line.Tax2, 0));
  const tax = round2(readNumber(line.Tax, tax1 + tax2));

  // The deducted tax counts only when it matches what the deduction rate gives.
  let nonRecoverableTax = 0;
  const deduction = readNumber(taxDeduction, 0);
  if (deduction > 0) {
    if (deduction >= 1) {
      throw new RangeError('buildAmountsSummary: a tax deduction of 100% or more has no amounts_summary');
    }
    const originalTax = round2(tax / (1 - deduction));
    const taxDeducted = round2(readNumber(line.TaxDeducted, 0));
    if (round2(originalTax - tax) === taxDeducted) {
      nonRecoverableTax = taxDeducted;
    }
  }

  const subTotal = round2(readNumber(line.SubTotal, readNumber(line.Total, 0))) - nonRecoverableTax;

  return {
    ItemSubTotal: subTotal,
    ItemTotal: total,
    ItemTax1: tax1,
    ItemTax2: tax2,
    ItemTax: tax,
    ItemNonRecovTax: nonRecoverableTax,
    ItemTotalWithTax: total + tax1 + tax2,
    TipAllocatedToItem: round2(readNumber(line.TipAllocated, 0)),
  };
}

// Reads a number the way the server's `safe_to_float` does: a missing, empty or
// unreadable value takes the fallback. `Number('')` alone would give 0 instead.
// Two known differences from the server: an infinite or NaN value takes the
// fallback here, and a few unusual strings parse differently ('0x10' is 16 here
// but unreadable in Python; '1_000' is 1000 in Python but unreadable here).
function readNumber(value, fallback) {
  const readable = typeof value === 'number'
    || typeof value === 'boolean'
    || (typeof value === 'string' && value.trim() !== '');
  const number = readable ? Number(value) : NaN;
  return Number.isFinite(number) ? number : fallback;
}

// Rounds to the cent as Python `round(x, 2)` does: on the exact stored value, and
// an exact tie goes to the even cent (0.125 gives 0.12, where Math.round gives 0.13).
export function round2(value) {
  const magnitude = Math.abs(value);
  // At two decimals, the only exact ties are odd numbers of eighths (…125, …375, …625, …875).
  if (Number.isInteger(magnitude * 8) && !Number.isInteger(magnitude * 4)) {
    const lowerCents = Math.floor(magnitude * 100);
    const evenCents = lowerCents % 2 === 0 ? lowerCents : lowerCents + 1;
    return (Math.sign(value) * evenCents) / 100;
  }
  // toFixed rounds the exact stored value: 0.015 is stored as 0.01499…, so it gives 0.01.
  return Math.sign(value) * Number(magnitude.toFixed(2));
}
